"use strict";
/**
 * Unified Firebase Functions Service
 * Provides a consistent API for Cloud Functions that works seamlessly across web and native platforms
 * - Automatically routes to Native Functions Plugin on iOS/Android
 * - Uses web Firebase SDK on browsers
 *
 * CRITICAL: This fixes iOS WKWebView timeout issues with long-running Cloud Functions
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.unifiedFunctions = exports.unifiedHttpsCallable = void 0;
const core_1 = require("@capacitor/core");
const functions_1 = require("firebase/functions");
const firebaseManager_1 = require("./firebaseManager");
/**
 * Check if native Firebase Functions plugin is available
 */
function isNativeFunctionsAvailable() {
    var _a;
    if (!core_1.Capacitor.isNativePlatform()) {
        return false;
    }
    // Check if the plugin is installed and available
    try {
        // Access plugins via window object in Capacitor
        const plugins = ((_a = window === null || window === void 0 ? void 0 : window.Capacitor) === null || _a === void 0 ? void 0 : _a.Plugins) || {};
        return !!plugins.FirebaseFunctions;
    }
    catch (_b) {
        return false;
    }
}
/**
 * Call a Cloud Function using the native plugin
 */
function callFunctionNative(functionName, data, options) {
    var _a;
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const plugins = ((_a = window === null || window === void 0 ? void 0 : window.Capacitor) === null || _a === void 0 ? void 0 : _a.Plugins) || {};
            const FirebaseFunctions = plugins.FirebaseFunctions;
            if (!FirebaseFunctions) {
                throw new Error('Native Firebase Functions plugin not found');
            }
            console.log(`📱 UnifiedFunctions: Calling NATIVE function: ${functionName}`, {
                hasData: !!data,
                timeout: options === null || options === void 0 ? void 0 : options.timeout,
                region: options === null || options === void 0 ? void 0 : options.region
            });
            // Call native plugin using @capacitor-firebase/functions API
            // The plugin uses `callByName` method per the official documentation
            const result = yield FirebaseFunctions.callByName({
                name: functionName,
                data: data || {},
                timeout: options === null || options === void 0 ? void 0 : options.timeout,
                region: (options === null || options === void 0 ? void 0 : options.region) || 'us-central1'
            });
            console.log(`✅ UnifiedFunctions: Native call successful for ${functionName}`);
            return { data: result.data };
        }
        catch (error) {
            console.error(`❌ UnifiedFunctions: Native call failed for ${functionName}:`, error);
            throw new Error((error === null || error === void 0 ? void 0 : error.message) || 'Cloud Function call failed');
        }
    });
}
/**
 * Call a Cloud Function using the web SDK
 */
function callFunctionWeb(functionName, data, options) {
    return __awaiter(this, void 0, void 0, function* () {
        console.log(`🌐 UnifiedFunctions: Calling WEB function: ${functionName}`, {
            hasData: !!data,
            timeout: options === null || options === void 0 ? void 0 : options.timeout
        });
        // Get Functions instance from firebaseManager
        const functions = yield firebaseManager_1.firebaseManager.getFunctions();
        // Create callable reference
        const callable = (0, functions_1.httpsCallable)(functions, functionName, options);
        // Call the function
        const result = yield callable(data);
        console.log(`✅ UnifiedFunctions: Web call successful for ${functionName}`);
        return result;
    });
}
/**
 * Create a unified Cloud Functions callable that works on both web and native platforms
 *
 * Usage:
 * ```typescript
 * const generateRecs = unifiedHttpsCallable<ReqData, ResData>('generateHybridRecommendations', { timeout: 60000 });
 * const result = await generateRecs({ questionnaire, tasteProfile });
 * ```
 */
function unifiedHttpsCallable(functionName, options) {
    const isNative = isNativeFunctionsAvailable();
    console.log(`🔧 UnifiedFunctions: Creating callable for "${functionName}"`, {
        isNative,
        platform: core_1.Capacitor.getPlatform(),
        timeout: options === null || options === void 0 ? void 0 : options.timeout,
        region: options === null || options === void 0 ? void 0 : options.region
    });
    return (data) => __awaiter(this, void 0, void 0, function* () {
        if (isNative) {
            // Use native plugin for iOS/Android
            return callFunctionNative(functionName, data, options);
        }
        else {
            // Use web SDK for browsers
            return callFunctionWeb(functionName, data, options);
        }
    });
}
exports.unifiedHttpsCallable = unifiedHttpsCallable;
/**
 * Export for backward compatibility and direct access
 */
exports.unifiedFunctions = {
    httpsCallable: unifiedHttpsCallable
};
